import { apiFetch } from "@/lib/api";

export type QcIncidentType = "device" | "clamp" | "pcba";
export type QcIncidentSubtype = "large" | "small";
export type QcIncidentPcbaProductFamily =
  | "fi_3c_b"
  | "fi_3c_q"
  | "fi_3c_n"
  | "fi_5a_n"
  | "fi_5a_w"
  | "dm_1";
export type QcIncidentPcbaTlaVariant = "A" | "C";
export type QcIncidentResolution = "repair" | "scrap" | "touch_sample";
/** Grade recorded when potting rework returns (Final QC hub or QC incidents hub). */
export type PottingRegradeGrade = "A" | "B" | "C" | "Fail";

export interface PottingRework {
  id: number;
  incidentId: number;
  serialFciId: number | null;
  routedAt: string | null;
  routedBy: string;
  routedByStored: string;
  purchaseOrderId: number | null;
  purchaseOrderMvId: number | null;
  purchaseOrderMvNumber: string | null;
  regradedAt: string | null;
  regradePottingGrade: PottingRegradeGrade | null;
  regradeNotes: string | null;
  cartonNumber?: number | null;
  cartonAssignedBy?: string | null;
  laborSku?: string | null;
  productLabel?: string | null;
  internalSend: boolean;
  sentAt?: string | null;
}

export interface PottingReworkQueueItem extends PottingRework {
  serial: string | null;
  issue: string | null;
  issueDate: string | null;
  repairScrap: QcIncidentResolution | null;
  resolutionDate: string | null;
  cartonNumber: number | null;
  cartonAssignedBy: string | null;
  laborSku: string | null;
  productLabel: string | null;
}

export interface PottingReworkCartonSummary {
  cartonNumber: number;
  laborSku: string;
  productLabel: string;
  sealed: boolean;
  sealedAt: string | null;
  labelPrintedAt?: string | null;
  rowCount: number;
}

export interface QcIncident {
  id: number;
  serialFciId: number | null;
  serial: string | null;
  deviceLabelSerial: string | null;
  boxSerial: string | null;
  clampSerial: string | null;
  pcbaSerial: string | null;
  pcbaProductFamily: QcIncidentPcbaProductFamily | null;
  pcbaTlaVariant: QcIncidentPcbaTlaVariant | null;
  type: QcIncidentType | null;
  subtype: QcIncidentSubtype | null;
  issue: string | null;
  issueNotes: string | null;
  issueDate: string | null;
  /** Directory-style label for UI (display name when known). */
  issuePerson: string | null;
  /** Raw `issue_person` from database (login email for hub-filed rows when known). */
  issuePersonStored: string | null;
  repairScrap: QcIncidentResolution | null;
  resolutionDate: string | null;
  resolutionNotes: string | null;
  resolutionPerson: string | null;
  resolutionPersonStored: string | null;
  rootCause: string | null;
  tier1Issue: string | null;
  tier1IssuePerson: string | null;
  tier1IssueAt: string | null;
  tier2Issue: string | null;
  tier2IssuePerson: string | null;
  tier2IssueAt: string | null;
  locationId: number;
  status: "resolved" | "unresolved";
  pottingRework: PottingRework | null;
  /** Present on create, after the incident insert mints a quality event. */
  qualityEventId?: number | null;
  /** Operator scripts linked to the failure modes saved on create. */
  operatorScripts?: QcOperatorScriptRun[];
}

export interface QcOperatorScriptOption {
  label: string;
  outcome: "repair" | "scrap" | "samples_only" | "tier1";
  followUp: string | null;
}

export interface QcOperatorScriptRun {
  id: number;
  name: string;
  question: string | null;
  steps: string;
  options: QcOperatorScriptOption[];
}

export function applyQcOperatorOutcome(
  incidentId: number,
  outcome: "tier1" | "repair" | "scrap" | "samples_only",
  note: string,
  responses: { scriptName: string; answerLabel: string; outcome: "tier1" | "repair" | "scrap" | "samples_only" }[],
): Promise<{ incident: QcIncident; message: string }> {
  return apiFetch<{ incident: QcIncident; message: string }>(
    `/api/manufacturing/qc/incidents/${encodeURIComponent(String(incidentId))}/operator-outcome`,
    { method: "POST", body: { outcome, note, responses } },
  );
}

export interface RootCause {
  cause: string;
  display_name: string | null;
}

export interface QcIncidentRepairReturnGuidance {
  previousStationName: string;
  destinationStationName: string;
  detail: string;
  hubPath: string | null;
}

export interface QcIncidentDetail extends QcIncident {
  repairReturnGuidancePreview?: QcIncidentRepairReturnGuidance | null;
}

export interface ResolveIncidentResult extends QcIncident {
  repairReturnGuidance?: QcIncidentRepairReturnGuidance | null;
}

export interface OpenIncidentInput {
  type: QcIncidentType;
  subtype?: QcIncidentSubtype | null;
  serial: string;
  pcba_product_family?: QcIncidentPcbaProductFamily | null;
  pcba_tla_variant?: QcIncidentPcbaTlaVariant | null;
  issue: string;
  issue_notes?: string | null;
  tier1_issue?: string | null;
  issue_date?: string | null;
  device_label_serial?: string | null;
  clamp_serial?: string | null;
  mcu_uuid?: string | null;
  failureModeIds?: number[];
}

export const PCBA_PRODUCT_FAMILY_OPTIONS: {
  value: QcIncidentPcbaProductFamily;
  label: string;
  group: "FI-3C" | "FI-5A" | "DM-1";
}[] = [
  { value: "fi_3c_b", label: "FI-3C-B", group: "FI-3C" },
  { value: "fi_3c_q", label: "FI-3C-Q", group: "FI-3C" },
  { value: "fi_3c_n", label: "FI-3C-N", group: "FI-3C" },
  { value: "fi_5a_n", label: "FI-5A-N", group: "FI-5A" },
  { value: "fi_5a_w", label: "FI-5A-W", group: "FI-5A" },
  { value: "dm_1", label: "DM-1 PCBA", group: "DM-1" },
];

export interface ResolveIncidentInput {
  repair_scrap: QcIncidentResolution;
  resolution_date?: string | null;
  resolution_notes?: string | null;
  root_cause?: string | null;
  device_label_serial?: string | null;
  clamp_serial?: string | null;
}

export interface RecordRegradeInput {
  regrade_potting_grade: PottingRegradeGrade;
  regrade_notes?: string | null;
}

export const QC_INCIDENTS_QUERY_KEY = ["manufacturing", "qc-incidents"] as const;

export type QcIncidentStatusFilter = "unresolved" | "potting_rework" | "rework_failed" | "resolved";

export interface QcIncidentListStatusCounts {
  unresolved: number;
  potting_rework: number;
  rework_failed: number;
  resolved: number;
}

export interface QcIncidentListSummaryCounts {
  open: number;
  routed: number;
  resolved: number;
}

export interface QcIncidentListQuery {
  search?: string;
  status?: QcIncidentStatusFilter[];
  limit?: number;
  offset?: number;
}

export interface QcIncidentListResponse {
  rows: QcIncident[];
  total: number;
  limit: number;
  offset: number;
  statusCounts: QcIncidentListStatusCounts;
  summaryCounts: QcIncidentListSummaryCounts;
}
export const QC_ROOT_CAUSES_QUERY_KEY = ["manufacturing", "qc-root-causes"] as const;
export const POTTING_REWORK_QUERY_KEY = ["manufacturing", "qc-potting-rework"] as const;
export const POTTING_REWORK_CARTONS_QUERY_KEY = ["manufacturing", "qc-potting-rework", "cartons"] as const;

export function fetchIncidents(query: QcIncidentListQuery = {}): Promise<QcIncidentListResponse> {
  const params = new URLSearchParams();
  const search = query.search?.trim() ?? "";
  if (search !== "") {
    params.set("search", search);
  }
  if (query.status != null) {
    params.set("status", query.status.join(","));
  }
  if (query.limit != null) {
    params.set("limit", String(query.limit));
  }
  if (query.offset != null) {
    params.set("offset", String(query.offset));
  }
  const suffix = params.toString();
  return apiFetch<QcIncidentListResponse>(
    suffix === "" ? "/api/manufacturing/qc/incidents" : `/api/manufacturing/qc/incidents?${suffix}`,
  );
}

export function fetchIncident(id: number): Promise<QcIncidentDetail> {
  return apiFetch<QcIncidentDetail>(`/api/manufacturing/qc/incidents/${encodeURIComponent(String(id))}`);
}

export interface QcIncidentMessage {
  id: string;
  createdAt: string;
  authorUserId: string;
  authorEmail: string;
  authorDisplayName: string;
  body: string;
}

export function fetchQcIncidentMessages(incidentId: number): Promise<{ messages: QcIncidentMessage[] }> {
  return apiFetch<{ messages: QcIncidentMessage[] }>(
    `/api/manufacturing/qc/incidents/${encodeURIComponent(String(incidentId))}/messages`,
  );
}

export function addQcIncidentMessage(
  incidentId: number,
  body: string,
): Promise<{ messages: QcIncidentMessage[] }> {
  return apiFetch<{ messages: QcIncidentMessage[] }>(
    `/api/manufacturing/qc/incidents/${encodeURIComponent(String(incidentId))}/messages`,
    { method: "POST", body: { body } },
  );
}

export interface QcFailureModeOperatorScript {
  id: number;
  name: string;
  steps: string;
}

export interface QcFailureModeOption {
  id: number;
  category: string;
  name: string;
  operatorScripts: QcFailureModeOperatorScript[];
}

export function fetchQcFailureModes(): Promise<{ rows: QcFailureModeOption[] }> {
  return apiFetch<{ rows: QcFailureModeOption[] }>("/api/manufacturing/qc/failure-modes");
}

export function fetchQcIncidentIssueTiers(): Promise<{ tier1: string[]; tier2: string[] }> {
  return apiFetch<{ tier1: string[]; tier2: string[] }>("/api/manufacturing/qc/issue-tiers");
}

export function saveQcIncidentIssueTiers(
  incidentId: number,
  input: { tier1_issue?: string | null; tier2_issue?: string | null },
): Promise<QcIncident> {
  return apiFetch<QcIncident>(
    `/api/manufacturing/qc/incidents/${encodeURIComponent(String(incidentId))}/issue-tiers`,
    { method: "PATCH", body: input },
  );
}

export function fetchRootCauses(): Promise<{ rows: RootCause[] }> {
  return apiFetch<{ rows: RootCause[] }>("/api/manufacturing/qc/root-causes");
}

export function createRootCause(label: string): Promise<RootCause> {
  return apiFetch<RootCause>("/api/manufacturing/qc/root-causes", {
    method: "POST",
    body: { label },
  });
}

export function fetchPottingReworkQueue(): Promise<{ rows: PottingReworkQueueItem[] }> {
  return apiFetch<{ rows: PottingReworkQueueItem[] }>("/api/manufacturing/qc/potting-rework");
}

export function openIncident(input: OpenIncidentInput): Promise<QcIncident> {
  return apiFetch<QcIncident>("/api/manufacturing/qc/incidents", {
    method: "POST",
    body: input,
  });
}

export interface QcIncidentSerialHints {
  serial: string;
  suggestedType: QcIncidentType | null;
  suggestedSubtype: QcIncidentSubtype | null;
  pcbaProductFamily: QcIncidentPcbaProductFamily | null;
  pcbaTlaVariant: QcIncidentPcbaTlaVariant | null;
  openIncidentId: number | null;
  serialFciId: number | null;
}

export function fetchQcIncidentSerialHints(serial: string): Promise<QcIncidentSerialHints> {
  const params = new URLSearchParams({ serial: serial.trim() });
  return apiFetch<QcIncidentSerialHints>(`/api/manufacturing/qc/incidents/serial-hints?${params.toString()}`);
}

export function resolveIncident(id: number, input: ResolveIncidentInput): Promise<ResolveIncidentResult> {
  return apiFetch<ResolveIncidentResult>(`/api/manufacturing/qc/incidents/${encodeURIComponent(String(id))}/resolve`, {
    method: "PATCH",
    body: input,
  });
}

export type PottingReworkSendFlow = "rework" | "internal_rework";

export function routePottingRework(
  id: number,
  reworkPath: "scs" | "internal",
): Promise<QcIncident> {
  return apiFetch<QcIncident>(`/api/manufacturing/qc/incidents/${encodeURIComponent(String(id))}/potting-rework`, {
    method: "POST",
    body: { reworkPath },
  });
}

export function recordRegrade(id: number, input: RecordRegradeInput): Promise<QcIncident> {
  return apiFetch<QcIncident>(`/api/manufacturing/qc/potting-rework/${encodeURIComponent(String(id))}/regrade`, {
    method: "PATCH",
    body: input,
  });
}

export function fetchPottingReworkCartons(
  sendFlow: PottingReworkSendFlow = "rework",
): Promise<{ cartons: PottingReworkCartonSummary[] }> {
  const params = new URLSearchParams({ sendFlow });
  return apiFetch<{ cartons: PottingReworkCartonSummary[] }>(
    `/api/manufacturing/qc/potting-rework/cartons?${params.toString()}`,
    {
    cache: "no-store",
    },
  );
}

export function assignPottingReworkCarton(reworkId: number): Promise<{
  ok: boolean;
  reworkId: number;
  cartonNumber: number;
  laborSku: string;
  productLabel: string;
  createdNewCarton?: boolean;
}> {
  return apiFetch(`/api/manufacturing/qc/potting-rework/${encodeURIComponent(String(reworkId))}/assign-carton`, {
    method: "POST",
    body: {},
  });
}

export function sealPottingReworkCarton(
  cartonNumber: number,
  sendFlow: PottingReworkSendFlow = "rework",
): Promise<{ ok: boolean; cartonNumber: number; sealed: boolean }> {
  const params = new URLSearchParams({ sendFlow });
  return apiFetch(`/api/manufacturing/qc/potting-rework/cartons/${cartonNumber}/seal?${params.toString()}`, {
    method: "POST",
    body: {},
  });
}

export function printPottingReworkCarton(
  cartonNumber: number,
  printer: string,
  sendFlow: PottingReworkSendFlow = "rework",
): Promise<{ printed: boolean; lineCount: number }> {
  const params = new URLSearchParams({ sendFlow });
  return apiFetch(`/api/manufacturing/qc/potting-rework/cartons/${cartonNumber}/print?${params.toString()}`, {
    method: "POST",
    body: { printer },
  });
}
